import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DynamicObjectInterface } from '../../../../sdk/type';
import {
  AdminPageHeader,
  ButtonStyles,
  Input,
  InputTypes,
  MLPVersion,
  Theme,
  RadioButton,
  InputValidations,
} from '../../../../ui/components';
import { LoggerService } from '../../../../ui/utils';
import { useAdminToast } from '../../../../ui/components/AdminToast/AdminToast';
import { useAPICall } from '../../../../hooks';
import RewardService from '../../../../sdk/services/rewardService';
import styles from './index.module.scss';

export interface RewardData {
  recId: number;
  rewardCode: string;
  rewardName: string;
  imageURL: string;
  status: string;
}

export const AdminRewardEditPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { adminToast } = useAdminToast();
  const id = searchParams.get('');
  const [rewardData, setRewardData] = useState<RewardData | null>(null);
  const [clickedFields, setClickedFields] = useState({
    rewardCode: false,
    rewardName: false,
    // imageURL: false,
    // status: false,
  });
  const [invalid, setInvalid] = useState(true);

  const apiCall = useAPICall();
  const isNew = id === 'new';

  // Use useEffect to update rewardData when id changes
  useEffect(() => {
    (async () => {
      if (!isNew) {
        try {
          const response = await apiCall(RewardService.getRewardDetails, `${id}`);
          setRewardData(response?.data?.data ?? {});
        } catch (error) {
          LoggerService.log('Failed to retrieve reward list', error);
        }
      }
    })();
  }, [id, isNew]);

  useEffect(() => {
    validateInputs();
  }, [rewardData]);

  const handleInputChange = (name: string, value: DynamicObjectInterface) => {
    setRewardData(prevRewardData => ({
      ...prevRewardData!,
      [name]: value,
    }));

    setClickedFields(prevClickedFields => ({
      ...prevClickedFields,
      [name]: value === '',
    }));
  };

  const validateInputs = () => {
    const { rewardCode, rewardName } = rewardData ?? {};
    const isAnyFieldEmpty = !rewardCode || !rewardName;
    setInvalid(isAnyFieldEmpty);
  };

  const onSave = async () => {
    const payload = {
      rewardCode: rewardData?.rewardCode,
      rewardName: rewardData?.rewardName,
      // imageURL: rewardData?.imageURL,
      createdBy: '',
      modifiedBy: '',
      status: rewardData?.status,
    };

    let result = isNew
      ? await apiCall(RewardService.createReward, payload)
      : await apiCall(RewardService.updateReward, `${id}`, payload);
    if (result.status === 200 && result?.data?.status) {
      navigate(`/admin/master/reward-setup/list`);
      adminToast({
        message: result?.data?.message,
        theme: Theme.SUCCESS,
      });
    } else {
      adminToast({
        message: result?.data?.message,
        theme: Theme.ERROR,
      });
    }
  };

  const buttonConfig = [
    {
      title: t('admin.rewardEdit.cancel'),
      buttonStyle: ButtonStyles.SOLID,
      onClick: () => {
        navigate(`/admin/master/reward-setup/list`);
      },
    },
    {
      title: t('admin.rewardEdit.save'),
      buttonStyle: ButtonStyles.SOLID,
      disabled: invalid,
      onClick: () => {
        onSave();
      },
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title={isNew ? t('admin.rewardEdit.newTitle') : t('admin.rewardEdit.editTitle')}
        button={buttonConfig}
      />
      <div className={styles['edit-reward-form-container']}>
        {/* <div className={styles['input-container']}>
          <Input
            key={id}
            disabled={!isNew}
            type={InputTypes.TEXT}
            name={'recId'}
            label={'RecId'}
            value={rewardData?.recId}
            mlpVersion={MLPVersion.ONE}
          />
        </div> */}
        <div className={styles['input-container']}>
          <Input
            key={id}
            disabled={!isNew}
            type={InputTypes.TEXT}
            name={'rewardCode'}
            label={`${t('admin.rewardMaster.rewardCode')}*`}
            value={rewardData?.rewardCode}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              handleInputChange('rewardCode', e.target.value);
            }}
            onClick={() =>
              setClickedFields(prevClickedFields => ({
                ...prevClickedFields,
                rewardCode: rewardData?.rewardCode === '',
              }))
            }
            status={clickedFields.rewardCode ? InputValidations.INVALID : InputValidations.DEFAULT}
            message={clickedFields.rewardCode ? `${t('admin.rewardMaster.requiredRewardCode')}` : ''}
          />
        </div>
        <div className={styles['input-container']}>
          <Input
            key={id}
            type={InputTypes.TEXT}
            name={'rewardName'}
            label={`${t('admin.rewardMaster.rewardName')}*`}
            value={rewardData?.rewardName}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              handleInputChange('rewardName', e.target.value);
            }}
            onClick={() =>
              setClickedFields(prevClickedFields => ({
                ...prevClickedFields,
                rewardName: rewardData?.rewardName === '',
              }))
            }
            status={clickedFields.rewardName ? InputValidations.INVALID : InputValidations.DEFAULT}
            message={clickedFields.rewardName ? `${t('admin.rewardMaster.requiredRewardName')}` : ''}
          />
        </div>

        <div className={styles['input-container']}>
          <Input
            key={id}
            type={InputTypes.TEXT}
            name="imageURL"
            label={`${t('admin.rewardMaster.imageURL')}`}
            disabled
            value={rewardData?.imageURL}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              handleInputChange('imageURL', e.target.value);
            }}
            onClick={() =>
              setClickedFields(prevClickedFields => ({
                ...prevClickedFields,
                imageURL: rewardData?.imageURL === '',
              }))
            }
            message={`${t('admin.rewardMaster.imageURLNotes')}`}
          />
        </div>

        {!isNew ? (
          <div className={styles['input-container']}>
            <div className={styles['input']}>
              <div className={styles['input-label']}>{`${t('admin.rewardMaster.status')}`}</div>
              <div className={styles['radio-button-container']}>
                <RadioButton
                  label="Active"
                  checked={rewardData?.status === 'ACTIVE'}
                  onClick={() => {
                    handleInputChange('status', 'ACTIVE');
                  }}
                />
                <RadioButton
                  label="Inactive"
                  checked={rewardData?.status === 'INACTIVE'}
                  onClick={() => {
                    handleInputChange('status', 'INACTIVE');
                  }}
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
